import { zodResolver } from '@hookform/resolvers/zod';
import InfoIcon from '@mui/icons-material/Info';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { POST_SIZE_OPTIONS, WidthMeasurementReference } from '../building-constants';
import { EditableBuildingName } from './editable-building-name';

// Validation schema for building information form
const buildingInformationSchema = z.object({
  width: z
    .union([
      z
        .number()
        .int('Enter a valid whole number for building width')
        .min(0, 'Building width must be 0 or greater')
        .max(999, 'Building width must be 999 or less'),
      z.undefined(),
    ])
    .optional(),
  length: z
    .union([
      z
        .number()
        .int('Enter a valid whole number for building length')
        .min(0, 'Building length must be 0 or greater')
        .max(999, 'Building length must be 999 or less'),
      z.undefined(),
    ])
    .optional(),
  height: z
    .union([
      z
        .number()
        .int('Enter a valid whole number for building height')
        .min(0, 'Building height must be 0 or greater')
        .max(99, 'Building height must be 99 or less'),
      z.undefined(),
    ])
    .optional(),
  baySpacing: z
    .number({
      required_error: 'Bay spacing is required',
      invalid_type_error: 'Bay spacing must be a number',
    })
    .int('Bay spacing must be a whole number')
    .min(1, 'Bay spacing must be at least 1'),
  postSize: z.string().min(1, 'Post size is required'),
  widthMeasurementReference: z.nativeEnum(WidthMeasurementReference).optional(),
  application: z.enum(['Residential', 'Agricultural'], {
    errorMap: () => ({ message: 'Application is required' }),
  }),
  additionalNotes: z
    .union([
      z.string().max(2000, 'Additional notes must be 2000 characters or less'),
      z.literal(''),
      z.undefined(),
    ])
    .optional(),
  isPermanentDwelling: z.boolean(),
  includePermanentDwellingWithPoleBarn: z.boolean().nullable().optional(),
  includePermanentDwellingForPartiallyClosedPoleBarn: z.boolean().nullable().optional(),
  includePermanentDwellingForFullyEnclosedPoleBarn: z.boolean().nullable().optional(),
  includePermanentDwellingWithNoMEP: z.boolean().nullable().optional(),
});

export type BuildingFormData = z.infer<typeof buildingInformationSchema>;

interface EngineeringFees {
  openPoleBarnFee: number;
  partialClosedPoleBarnFee: number;
  fullyEnclosedPoleBarnFee: number;
  permanentDwellingFee: number;
}

export interface CartProps {
  buildingTotal?: number;
  gableTotal?: number;
  headerTotal?: number;
  leanToTotal?: number;
  onUpdateCart?: () => void;
  onRemoveFromCart?: () => void;
  isSubmitting?: boolean;
  hasTrussChanges?: boolean;
}

interface BuildingInformationFormProps {
  buildingName: string;
  buildingId: number;
  existingBuildingNames: string[];
  formData: BuildingFormData | null;
  engineeringFees?: EngineeringFees;
  onFormDataChange: (data: BuildingFormData) => void;
  onSave: (data: BuildingFormData) => void;
  cartProps?: CartProps;
  onUpdateBuildingName?: (buildingId: number, newName: string) => Promise<void>;
  isUpdatingBuildingName?: boolean;
  readOnly?: boolean;
  formResetKey?: number;
}

export const BuildingInformationForm: React.FC<BuildingInformationFormProps> = ({
  buildingName,
  buildingId,
  existingBuildingNames,
  formData,
  engineeringFees,
  onFormDataChange,
  onSave,
  cartProps,
  onUpdateBuildingName,
  isUpdatingBuildingName = false,
  readOnly = false,
  formResetKey = 0,
}) => {
  // Extract cart props with defaults
  const {
    buildingTotal,
    gableTotal,
    headerTotal,
    leanToTotal,
    onUpdateCart,
    onRemoveFromCart,
    isSubmitting = false,
    hasTrussChanges = true,
  } = cartProps || {};
  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isDirty },
  } = useForm<BuildingFormData>({
    resolver: zodResolver(buildingInformationSchema),
    mode: 'onTouched',
    defaultValues: formData || {
      baySpacing: 0,
      postSize: '6x6',
      application: 'Residential',
      isPermanentDwelling: false,
    },
  });

  const additionalNotesTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  // Marks formData updates that originate from this form's own edits (pushed up via
  // the watch subscription below) so the reset effect can ignore those echoes.
  const isInternalUpdateRef = useRef(false);

  // Reset the form only on genuine external data changes: the initial load of a
  // building, switching buildings, or a post-save refetch (formResetKey bump).
  //
  // We must NOT reset on the "echo" formData updates that the watch subscription
  // pushes back up while the user is typing. Doing so re-baselines the form and
  // clears RHF's dirty flag mid-edit, which made single-field edits (e.g. Bay
  // Spacing, or Additional Notes) silently revert and become un-saveable after an
  // initial save — the user had to also change Post Size to keep the form dirty.
  useEffect(() => {
    if (!formData) return;

    if (isInternalUpdateRef.current) {
      isInternalUpdateRef.current = false;
      return;
    }

    // formData identity changes on every external update; formResetKey forces a
    // reset even when the refetched values are byte-for-byte identical.
    reset(formData);
  }, [formData, formResetKey, reset]);

  // Keep parent state in sync with form edits so that sibling actions
  // (e.g. "Update Cart") always see the latest form values without requiring
  // an explicit save first. Flag these as internal so the reset effect ignores them.
  useEffect(() => {
    const subscription = watch(value => {
      isInternalUpdateRef.current = true;
      onFormDataChange(value as BuildingFormData);
    });
    return () => subscription.unsubscribe();
  }, [watch, onFormDataChange]);

  if (!formData) {
    return null;
  }

  const onSubmit = async (data: BuildingFormData) => {
    // Update parent with latest form data before saving
    onFormDataChange(data);
    // Call the save handler with the latest form data
    onSave(data);
  };

  // Handle form submission errors (validation errors)
  const onError = (errors: Record<string, unknown>) => {
    console.error('Form validation errors:', errors);
  };

  const isPermanentDwelling = watch('isPermanentDwelling');

  return (
    <Paper
      component="form"
      onSubmit={handleSubmit(onSubmit, onError)}
      sx={{
        position: 'relative',
        p: 3,
        pt: 4,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Building Name Label - Editable */}
      {onUpdateBuildingName ? (
        <EditableBuildingName
          name={buildingName}
          buildingId={buildingId}
          existingNames={existingBuildingNames}
          onSave={onUpdateBuildingName}
          isUpdating={isUpdatingBuildingName}
          variant="form"
        />
      ) : (
        <Typography
          variant="subtitle1"
          component="h2"
          fontWeight="500"
          sx={{
            position: 'absolute',
            top: 0,
            transform: 'translateY(-50%)',
            backgroundColor: 'background.paper',
            px: 1,
            ml: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: 'calc(100% - 2rem)',
          }}
        >
          {buildingName}
        </Typography>
      )}

      <Grid container spacing={3} sx={{ alignItems: 'stretch' }}>
        {/* Left side: Three original sections maintaining their original positions */}
        <Grid item xs={12} md={8}>
          <Grid container spacing={3} sx={{ alignItems: 'stretch' }}>
            {/* Building Dimensions Column */}
            <Grid item xs={12} md={4}>
              <Paper
                sx={{
                  p: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Building Dimensions
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Controller
                      name="width"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          fullWidth
                          label="Building Width (ft.)"
                          type="number"
                          value={field.value ?? ''}
                          onChange={e => {
                            const inputValue = e.target.value;
                            if (inputValue === '') {
                              field.onChange(undefined);
                            } else {
                              const numValue = parseInt(inputValue, 10);
                              if (!isNaN(numValue)) {
                                field.onChange(numValue);
                              }
                            }
                          }}
                          onBlur={e => {
                            // Allow empty values - don't force to 0
                            if (e.target.value === '') {
                              field.onChange(undefined);
                            }
                            field.onBlur();
                          }}
                          error={!!errors.width}
                          helperText={errors.width?.message}
                          InputProps={{ inputProps: { min: 0, max: 999 } }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      name="length"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          fullWidth
                          label="Building Length (ft.)"
                          type="number"
                          value={field.value ?? ''}
                          onChange={e => {
                            const inputValue = e.target.value;
                            if (inputValue === '') {
                              field.onChange(undefined);
                            } else {
                              const numValue = parseInt(inputValue, 10);
                              if (!isNaN(numValue)) {
                                field.onChange(numValue);
                              }
                            }
                          }}
                          onBlur={e => {
                            // Allow empty values - don't force to 0
                            if (e.target.value === '') {
                              field.onChange(undefined);
                            }
                            field.onBlur();
                          }}
                          error={!!errors.length}
                          helperText={errors.length?.message}
                          InputProps={{ inputProps: { min: 0, max: 999 } }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      name="height"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          fullWidth
                          label="Building Height (ft.)"
                          type="number"
                          value={field.value ?? ''}
                          onChange={e => {
                            const inputValue = e.target.value;
                            if (inputValue === '') {
                              field.onChange(undefined);
                            } else {
                              const numValue = parseInt(inputValue, 10);
                              if (!isNaN(numValue)) {
                                field.onChange(numValue);
                              }
                            }
                          }}
                          onBlur={e => {
                            // Allow empty values - don't force to 0
                            if (e.target.value === '') {
                              field.onChange(undefined);
                            }
                            field.onBlur();
                          }}
                          error={!!errors.height}
                          helperText={errors.height?.message}
                          InputProps={{ inputProps: { min: 0, max: 99 } }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      name="baySpacing"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          fullWidth
                          label="Bay Spacing (ft.)"
                          type="number"
                          value={field.value ?? ''}
                          onChange={e => {
                            const inputValue = e.target.value;
                            if (inputValue === '') {
                              field.onChange(1);
                            } else {
                              const numValue = parseInt(inputValue, 10);
                              if (!isNaN(numValue)) {
                                field.onChange(numValue);
                              }
                            }
                          }}
                          onBlur={field.onBlur}
                          error={!!errors.baySpacing}
                          helperText={errors.baySpacing?.message}
                          InputProps={{ inputProps: { min: 1 } }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      name="postSize"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth error={!!errors.postSize}>
                          <FormLabel>Post Size (ft.)</FormLabel>
                          <Select {...field}>
                            {POST_SIZE_OPTIONS.map(value => (
                              <MenuItem key={value} value={value}>
                                {value}
                              </MenuItem>
                            ))}
                          </Select>
                          {errors.postSize && (
                            <FormHelperText>{errors.postSize.message}</FormHelperText>
                          )}
                        </FormControl>
                      )}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Other Column */}
            <Grid item xs={12} md={4}>
              <Paper
                sx={{
                  p: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Other
                </Typography>
                <Controller
                  name="application"
                  control={control}
                  render={({ field }) => (
                    <FormControl component="fieldset" sx={{ mb: 2 }} error={!!errors.application}>
                      <FormLabel component="legend">Application</FormLabel>
                      <RadioGroup row {...field}>
                        <FormControlLabel
                          value="Residential"
                          control={<Radio />}
                          label="Residential"
                        />
                        <FormControlLabel
                          value="Agricultural"
                          control={<Radio />}
                          label="Agricultural"
                        />
                      </RadioGroup>
                      {errors.application && (
                        <FormHelperText>{errors.application.message}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
                <Controller
                  name="additionalNotes"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      fullWidth
                      label="Additional Notes"
                      multiline
                      rows={4}
                      value={field.value || ''}
                      onChange={e => {
                        field.onChange(e.target.value || undefined);
                      }}
                      onBlur={field.onBlur}
                      name={field.name}
                      inputRef={(ref: HTMLTextAreaElement | null) => {
                        additionalNotesTextareaRef.current = ref;
                        // Set the field ref for react-hook-form
                        if (typeof field.ref === 'function') {
                          field.ref(ref);
                        }
                      }}
                      error={!!errors.additionalNotes}
                      helperText={
                        errors.additionalNotes?.message ||
                        `${(field.value || '').length}/2000 characters`
                      }
                      inputProps={{ maxLength: 2000 }}
                      sx={{ mb: 2 }}
                    />
                  )}
                />
                <Controller
                  name="isPermanentDwelling"
                  control={control}
                  render={({ field }) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                      <FormControlLabel
                        control={<Checkbox {...field} checked={field.value || false} />}
                        label="Permanent Dwelling"
                      />
                      <Tooltip
                        title={
                          <Box sx={{ p: 0.5 }}>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              A permanent dwelling is generally defined as a fixed residence
                              intended for continuous or long-term occupancy. It typically refers to
                              a building or structure, including homes, houses, mobile homes, or
                              units in multi-unit residential facilities, that are designed for
                              living and equipped with necessary amenities like plumbing,
                              electrical, heating, and air conditioning systems. Essentially, it's a
                              place where someone lives and stays, not a temporary or transient
                              accommodation.
                            </Typography>
                            <Typography variant="body2">
                              Truss US strongly suggests engineered building plans for all permanent
                              dwellings and they are required by local municipalities. While these
                              plans do not have to be purchased through Truss US, we do sell them.{' '}
                              <Typography
                                component="span"
                                variant="body2"
                                sx={{ fontWeight: 'bold' }}
                              >
                                See Engineered Plan Fees
                              </Typography>{' '}
                              section for more details.
                            </Typography>
                          </Box>
                        }
                        arrow
                        placement="right"
                        sx={{ maxWidth: 400 }}
                      >
                        <IconButton size="small" sx={{ p: 0.25, ml: -0.75 }}>
                          <InfoIcon fontSize="small" color="action" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                />
              </Paper>
            </Grid>

            {/* Engineered Plan Fees Column */}
            <Grid item xs={12} md={4}>
              <Paper
                sx={{
                  p: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Engineered Plan Fees
                </Typography>
                {engineeringFees ? (
                  <>
                    <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                      Please select which fees to include
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Controller
                        name="includePermanentDwellingWithPoleBarn"
                        control={control}
                        render={({ field }) => (
                          <FormControlLabel
                            control={
                              <Checkbox
                                {...field}
                                checked={field.value || false}
                                disabled={!isPermanentDwelling}
                              />
                            }
                            label={`Open Pole Barn - $${engineeringFees.openPoleBarnFee.toFixed(2)}`}
                          />
                        )}
                      />
                      <Controller
                        name="includePermanentDwellingForPartiallyClosedPoleBarn"
                        control={control}
                        render={({ field }) => (
                          <FormControlLabel
                            control={
                              <Checkbox
                                {...field}
                                checked={field.value || false}
                                disabled={!isPermanentDwelling}
                              />
                            }
                            label={`Partial Closed Pole Barn - $${engineeringFees.partialClosedPoleBarnFee.toFixed(
                              2
                            )}`}
                          />
                        )}
                      />
                      <Controller
                        name="includePermanentDwellingForFullyEnclosedPoleBarn"
                        control={control}
                        render={({ field }) => (
                          <FormControlLabel
                            control={
                              <Checkbox
                                {...field}
                                checked={field.value || false}
                                disabled={!isPermanentDwelling}
                              />
                            }
                            label={`Fully Enclosed Pole Barn - $${engineeringFees.fullyEnclosedPoleBarnFee.toFixed(
                              2
                            )}`}
                          />
                        )}
                      />
                      <Controller
                        name="includePermanentDwellingWithNoMEP"
                        control={control}
                        render={({ field }) => (
                          <FormControlLabel
                            control={
                              <Checkbox
                                {...field}
                                checked={field.value || false}
                                disabled={!isPermanentDwelling}
                              />
                            }
                            label={`Permanent Dwelling (no MEP) - $${engineeringFees.permanentDwellingFee.toFixed(
                              2
                            )}`}
                          />
                        )}
                      />
                    </Box>
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Engineering fees not available
                  </Typography>
                )}
              </Paper>
            </Grid>
          </Grid>
          {/* Save Button */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button
              type="submit"
              variant="contained"
              sx={{ py: 1.5, minWidth: 200 }}
              disabled={readOnly || !isDirty}
            >
              Save Building Information
            </Button>
          </Box>
        </Grid>

        {/* Building Total Column */}
        {cartProps && (
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="body2"
                  sx={{ mb: 0.5, color: 'text.secondary', fontWeight: 500 }}
                >
                  Building Total
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  ${(buildingTotal || 0).toFixed(2)}
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                  Totals by Truss Type:
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  Gable: ${(gableTotal || 0).toFixed(2)}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  Header: ${(headerTotal || 0).toFixed(2)}
                </Typography>
                <Typography variant="body2">Lean-to: ${(leanToTotal || 0).toFixed(2)}</Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {onUpdateCart && (
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={onUpdateCart}
                    disabled={readOnly || isSubmitting || !hasTrussChanges}
                    sx={{ py: 1.5 }}
                  >
                    {isSubmitting ? 'Updating...' : 'Update Cart'}
                  </Button>
                )}
                {onRemoveFromCart && (
                  <Button
                    variant="outlined"
                    color="error"
                    fullWidth
                    onClick={onRemoveFromCart}
                    disabled={readOnly}
                    sx={{ py: 1.5 }}
                  >
                    Remove from Cart
                  </Button>
                )}
              </Box>
            </Box>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};
